import { useState } from 'react';
import { Card, CardHeader, Button, Badge, Input, LoadingSkeleton } from '@nexora/design-system';
import {
  useModuleCtx,
  useProductivityAnalysis,
  type ProductivityTrend,
  TREND_LABEL,
} from './useOkrs';
import { formatNumber } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Análise de Produtividade e Eficiência
   (protótipo `erpone-an-lise-de-produtividade-e-efici-ncia`)
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (backend real, DTOs em okrs.dto.ts):
   - GET /okrs/productivity/analysis?departamento&periodo → ProductivityAnalysisDto
       { gargalo: GargaloIdentificadoDto|null, oportunidade: OportunidadeIdentificadaDto|null,
         scores: ScoreOperadorDto[], departamentos: ScoreDepartamentoDto[],
         periodoAtual: string|null }

   Score é 0-100 (número) — exibido como "94,2%". Tendência SUBINDO/ESTAVEL/CAINDO.
   ═══════════════════════════════════════════════════════════════════════════ */

function scorePercent(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
}

function trendColor(trend: ProductivityTrend): string {
  if (trend === 'SUBINDO') return '#2e7d32';
  if (trend === 'CAINDO') return '#ba1a1a';
  return '#74777f';
}

function trendIcon(trend: ProductivityTrend): string {
  if (trend === 'SUBINDO') return '↗';
  if (trend === 'CAINDO') return '↘';
  return '→';
}

export function ProductivityPage(): JSX.Element {
  const ctx = useModuleCtx();
  const [deptFilter, setDeptFilter] = useState('');
  const { data, isLoading, error } = useProductivityAnalysis(ctx, {
    departamento: deptFilter || undefined,
  });

  const scores = data?.scores ?? [];
  const departamentos = data?.departamentos ?? [];

  // Top scores para a lista "Team Efficiency Scores" do protótipo
  const topScores = [...scores].sort((a, b) => b.score - a.score);
  const piorOperador = scores.reduce<(typeof scores)[number] | null>(
    (acc, s) => (acc === null || s.score < acc.score ? s : acc),
    null,
  );

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>Análise de Produtividade</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Scores de eficiência por operador/célula {data?.periodoAtual ? `— período ${data.periodoAtual}` : ''}.
          </p>
        </div>
        <div style={{ width: '240px' }}>
          <Input
            label="Filtrar por departamento"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            placeholder="Ex.: Soldagem"
          />
        </div>
      </div>

      {/* Insights: Gargalo + Oportunidade */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <Card variant="outlined">
          <CardHeader>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1565c0' }}>Gargalo Identificado</h3>
          </CardHeader>
          <div style={{ padding: '0 1.25rem 1.25rem' }}>
            {isLoading && <LoadingSkeleton />}
            {error && <span style={{ color: '#ba1a1a', fontSize: '0.8125rem' }}>Indisponível no momento.</span>}
            {!isLoading && !error && !data?.gargalo && (
              <span style={{ color: '#74777f', fontSize: '0.875rem' }}>Nenhum gargalo identificado neste período.</span>
            )}
            {!isLoading && !error && data?.gargalo && (
              <div style={{ fontSize: '0.875rem', color: '#191c1e' }}>
                <p style={{ margin: '0 0 0.5rem' }}>
                  <strong>{data.gargalo.departamento}</strong> — pior operador: <strong>{data.gargalo.piorOperador}</strong>
                </p>
                <p style={{ margin: 0, color: '#43474e', fontSize: '0.8125rem' }}>
                  Score médio {scorePercent(data.gargalo.scoreMedio)} · {data.gargalo.operadores} operadores na célula.
                </p>
              </div>
            )}
          </div>
        </Card>
        <Card variant="outlined">
          <CardHeader>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2e7d32' }}>Oportunidade</h3>
          </CardHeader>
          <div style={{ padding: '0 1.25rem 1.25rem' }}>
            {isLoading && <LoadingSkeleton />}
            {error && <span style={{ color: '#ba1a1a', fontSize: '0.8125rem' }}>Indisponível no momento.</span>}
            {!isLoading && !error && !data?.oportunidade && (
              <span style={{ color: '#74777f', fontSize: '0.875rem' }}>Nenhuma oportunidade identificada neste período.</span>
            )}
            {!isLoading && !error && data?.oportunidade && (
              <div style={{ fontSize: '0.875rem', color: '#191c1e' }}>
                <p style={{ margin: '0 0 0.5rem' }}>
                  <strong>{data.oportunidade.departamento}</strong> — score {scorePercent(data.oportunidade.scoreMedio)}
                  {data.oportunidade.metaScore != null && <> vs meta {scorePercent(data.oportunidade.metaScore)}</>}
                </p>
                <p style={{ margin: 0, color: '#43474e', fontSize: '0.8125rem' }}>
                  Gap {scorePercent(data.oportunidade.gap)} · potencial de ganho estimado{' '}
                  <strong>{formatNumber(data.oportunidade.potencialGanho, { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}</strong>
                </p>
              </div>
            )}
          </div>
        </Card>
        {piorOperador && !isLoading && !error && (
          <Card variant="outlined">
            <CardHeader>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#ba1a1a' }}>Atenção</h3>
            </CardHeader>
            <div style={{ padding: '0 1.25rem 1.25rem', fontSize: '0.875rem', color: '#191c1e' }}>
              <p style={{ margin: '0 0 0.5rem' }}>
                Menor score: <strong>{piorOperador.operadorCelula}</strong> ({piorOperador.departamento})
              </p>
              <p style={{ margin: 0, color: '#43474e', fontSize: '0.8125rem' }}>
                {scorePercent(piorOperador.score)} · tendência {TREND_LABEL[piorOperador.tendencia].toLowerCase()}.
              </p>
            </div>
          </Card>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
        {/* Scores por departamento */}
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Scores por Departamento</h3>
          </CardHeader>
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
              <LoadingSkeleton />
              <span style={{ fontSize: '0.875rem' }}>Carregando análise...</span>
            </div>
          )}
          {error && !isLoading && (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
              <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar análise</p>
              <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
                {error instanceof Error ? error.message : 'Erro desconhecido'}
              </p>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>Tentar novamente</Button>
            </div>
          )}
          {!isLoading && !error && departamentos.length === 0 && (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f', fontSize: '0.875rem' }}>
              Nenhum departamento com score registrado.
            </div>
          )}
          {!isLoading && !error && departamentos.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    {['Departamento', 'Score Médio', 'Melhor Operador', 'Pior Operador', 'Variação (pp)', 'Tendência'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === 'Score Médio' || h === 'Variação (pp)' ? 'right' : 'left',
                          padding: '0.75rem 1rem',
                          background: '#f2f4f6',
                          color: '#43474e',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e0e3e5',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {departamentos.map((d) => (
                    <tr key={d.departamento} style={{ borderBottom: '1px solid #eceef0' }}>
                      <td style={{ padding: '0.625rem 1rem', fontWeight: 600 }}>{d.departamento}</td>
                      <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 600 }}>{scorePercent(d.scoreMedio)}</td>
                      <td style={{ padding: '0.625rem 1rem', color: '#43474e' }}>{d.melhorOperador ?? '—'}</td>
                      <td style={{ padding: '0.625rem 1rem', color: '#43474e' }}>{d.piorOperador ?? '—'}</td>
                      <td style={{ padding: '0.625rem 1rem', textAlign: 'right', color: d.variacao < 0 ? '#ba1a1a' : '#2e7d32' }}>
                        {d.variacao > 0 ? '+' : ''}{formatNumber(d.variacao, { maximumFractionDigits: 1 })}
                      </td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <Badge variant={d.tendencia === 'SUBINDO' ? 'success' : d.tendencia === 'CAINDO' ? 'error' : 'neutral'} size="sm">
                          {trendIcon(d.tendencia)} {TREND_LABEL[d.tendencia]}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Team Efficiency Scores */}
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Team Efficiency Scores</h3>
          </CardHeader>
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
              <LoadingSkeleton />
            </div>
          )}
          {!isLoading && !error && topScores.length === 0 && (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f', fontSize: '0.875rem' }}>
              Nenhum score de operador registrado.
            </div>
          )}
          {!isLoading && !error && topScores.length > 0 && (
            <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f2f4f6' }}>
                  <tr>
                    {['Operador/Célula', 'Score', 'Trend'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === 'Score' ? 'right' : 'left',
                          padding: '0.75rem 1rem',
                          color: '#43474e',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e0e3e5',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topScores.slice(0, 50).map((s) => (
                    <tr key={`${s.departamento}-${s.operadorCelula}`} style={{ borderBottom: '1px solid #eceef0' }}>
                      <td style={{ padding: '0.5rem 1rem', fontWeight: 500 }}>
                        {s.operadorCelula}
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#74777f' }}>{s.departamento}</span>
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {scorePercent(s.score)}
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'center', color: trendColor(s.tendencia), fontWeight: 600 }}>
                        {trendIcon(s.tendencia)}
                        <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem' }}>{TREND_LABEL[s.tendencia]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <style>{`.module-page { padding: 1.5rem; }`}</style>
    </div>
  );
}